import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/shared/components/ui/Button";
import { Logo } from "@/shared/components/ui/Logo";
import { Navigation } from "./Navigation";
import { MobileMenu } from "./MobileMenu";
import { NavigateDropdown } from "./NavigateDropdown";
import { ProfileDropdown } from "./ProfileDropdown";
import type { HeaderProps } from "./Header.types";
import type { RootState } from "@/app/store/store";
import { RiArrowDropDownLine } from "react-icons/ri";
import { FaRegBell } from "react-icons/fa6";
import { useGetNotificationsQuery } from "@/features/notifications/services/notificationApi";
import { NotificationDropdown } from "@/shared/components/ui/Notification";
import { SOCKET_EVENTS } from "@/shared/socket/socketEvents";
import { getSocket } from "@/shared/socket/socket";

export const Header: React.FC<HeaderProps> = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isNavigateDropdownOpen, setIsNavigateDropdownOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [liveUnreadCount, setLiveUnreadCount] = useState(0);
  const [profileImageError, setProfileImageError] = useState(false);
  const navigateDropdownRef = useRef<HTMLDivElement>(null);
  const profileDropdownRef = useRef<HTMLDivElement>(null);
  const notificationRef = useRef<HTMLDivElement>(null);

  // Get authentication status from Redux store
  const { isAuthenticated, user } = useSelector(
    (state: RootState) => state.auth,
  );

  const navigate = useNavigate();
  const location = useLocation();

  const onLoginClick = () => navigate("/login");
  const onSignupClick = () => navigate("/signup");

  const hideLogin = location.pathname === "/login";
  const hideSignup = location.pathname === "/signup";

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        navigateDropdownRef.current &&
        !navigateDropdownRef.current.contains(event.target as Node)
      ) {
        setIsNavigateDropdownOpen(false);
      }
      if (
        profileDropdownRef.current &&
        !profileDropdownRef.current.contains(event.target as Node)
      ) {
        setIsProfileDropdownOpen(false);
      }
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target as Node)
      ) {
        setIsNotificationOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const { data, refetch } = useGetNotificationsQuery(undefined, {
    skip: !isAuthenticated,
    pollingInterval: 0,
  });

  useEffect(() => {
    if (data?.data?.unreadCount !== undefined) {
      setLiveUnreadCount(data.data.unreadCount);
    }
  }, [data]);

  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    const handleNewNotification = () => {
      setLiveUnreadCount((prev) => Math.min(prev + 1, 99));
    };

    socket.on(SOCKET_EVENTS.NOTIFICATION.CREATED, handleNewNotification);

    return () => {
      socket.off(SOCKET_EVENTS.NOTIFICATION.CREATED, handleNewNotification);
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setLiveUnreadCount(0);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    setProfileImageError(false);
  }, [user?.profilePicture]);

  const unreadCount = liveUnreadCount;

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-[1010]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Left: Logo */}
          <div className="flex-shrink-0">
            <Link to="/dashboard" className="flex items-center">
              <Logo variant="horizontal" />
            </Link>
          </div>

          {/* Center: Desktop Navigation */}
          <div className="hidden md:block">
            <Navigation isAuthenticated={isAuthenticated} />
          </div>

          {/* Right: Actions Container */}
          <div className="flex items-center space-x-1 sm:space-x-4">
            {isAuthenticated ? (
              <>
                {/* Desktop-only items */}
                <div className="hidden md:flex items-center space-x-4">
                  {/* Navigate Dropdown */}
                  <div className="relative" ref={navigateDropdownRef}>
                    <button
                      type="button"
                      onClick={() => {
                        setIsNavigateDropdownOpen(!isNavigateDropdownOpen);
                        setIsProfileDropdownOpen(false);
                        setIsNotificationOpen(false);
                      }}
                      className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md whitespace-nowrap"
                    >
                      <span className="leading-none flex-shrink-0">
                        Navigate
                      </span>
                      <RiArrowDropDownLine
                        className={`w-5 h-5 flex-shrink-0 leading-none transition-transform duration-200 ${
                          isNavigateDropdownOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                    <NavigateDropdown
                      isOpen={isNavigateDropdownOpen}
                      onClose={() => setIsNavigateDropdownOpen(false)}
                    />
                  </div>
                </div>

                {/* Notifications */}
                <div className="relative" ref={notificationRef}>
                  <button
                    type="button"
                    onClick={() => {
                      const nextState = !isNotificationOpen;
                      setIsNotificationOpen(nextState);
                      if (nextState) {
                        refetch();
                      }
                      setIsNavigateDropdownOpen(false);
                      setIsProfileDropdownOpen(false);
                    }}
                    className="relative flex items-center justify-center p-2 text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                  >
                    <FaRegBell className="w-5 h-5" />
                    {unreadCount > 0 && (
                      <span className="absolute top-1 right-1 bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full border-2 border-white">
                        {unreadCount > 9 ? "9+" : unreadCount}
                      </span>
                    )}
                  </button>

                  <NotificationDropdown
                    isOpen={isNotificationOpen}
                    onClose={() => setIsNotificationOpen(false)}
                  />
                </div>

                {/* Desktop-only Profile */}
                <div
                  className="hidden md:block relative"
                  ref={profileDropdownRef}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setIsProfileDropdownOpen(!isProfileDropdownOpen);
                      setIsNavigateDropdownOpen(false);
                      setIsNotificationOpen(false);
                    }}
                    className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md whitespace-nowrap"
                  >
                    <div className="w-8 h-8 rounded-full overflow-hidden border border-gray-200 flex-shrink-0">
                      {user?.profilePicture && !profileImageError ? (
                        <img
                          src={user.profilePicture}
                          alt="profile"
                          className="w-full h-full object-cover"
                          onError={() => setProfileImageError(true)}
                        />
                      ) : (
                        <div className="w-full h-full bg-gray-300 flex items-center justify-center">
                          <span className="text-sm font-medium text-gray-700">
                            {user?.name?.charAt(0).toUpperCase() || "👤"}
                          </span>
                        </div>
                      )}
                    </div>
                    <span className="hidden lg:inline truncate max-w-[120px]">
                      {user?.name || "Profile"}
                    </span>
                    <RiArrowDropDownLine
                      className={`w-5 h-5 transition-transform duration-200 ${
                        isProfileDropdownOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  <ProfileDropdown
                    isOpen={isProfileDropdownOpen}
                    onClose={() => setIsProfileDropdownOpen(false)}
                    user={user}
                  />
                </div>
              </>
            ) : (
              /* Unauthenticated Desktop Actions */
              <div className="hidden md:flex items-center space-x-2">
                {!hideLogin && (
                  <Button variant="ghost" size="sm" onClick={onLoginClick}>
                    Login
                  </Button>
                )}
                {!hideSignup && (
                  <Button variant="primary" size="sm" onClick={onSignupClick}>
                    Sign Up
                  </Button>
                )}
              </div>
            )}

            {/* Mobile Hamburger Button */}
            <div className="md:hidden">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setIsMobileMenuOpen(!isMobileMenuOpen);
                  setIsNotificationOpen(false);
                }}
                className="p-2"
              >
                <span className="sr-only">Open main menu</span>
                <span className="text-2xl leading-none">
                  {isMobileMenuOpen ? "✕" : "☰"}
                </span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <MobileMenu
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        isAuthenticated={isAuthenticated}
        user={user}
        unreadCount={unreadCount}
        onNotificationClick={refetch}
      />
    </header>
  );
};;
